import { all, takeEvery, takeLatest, put, call, select, cancelled } from 'redux-saga/effects';
import FileSaver from 'file-saver';
import queryString from 'query-string';
import { AXIOS_CANCEL_CODE } from '../../config/axios';
import { getFileNameFromApiResponseHeader } from '../../helpers/utility';
import { selectPoliciesActiveFilters } from '../filters/selectors';
import {
  getPoliciesInsuredRisksFilterOptions,
  getPoliciesCustomFieldsFilterOptions,
  getPoliciesInsuredRiskTypesFilterOptions,
  getPoliciesInsuranceOrganizationsFilterOptions
} from '../filters/slice';
import { onGetPolicies, onExportPolicies } from './api';
import {
  getPolicies,
  getPoliciesSucceeded,
  getPoliciesFailed,
  searchPolicies,
  searchPoliciesSucceeded,
  searchPoliciesFailed,
  exportPolicies,
  exportPoliciesSucceeded,
  exportPoliciesFailed,
  reloadPolicies
} from './slice';

export function* getPoliciesWorker({ payload }) {
  const controller = new AbortController();

  try {
    const response = yield call(onGetPolicies, payload, controller.signal);

    const result = {
      policies: response.data,
      total: Number(response.headers.total) || 0
    };

    yield put(getPoliciesSucceeded(result));
  } catch (error) {
    if (error?.code !== AXIOS_CANCEL_CODE) {
      yield put(getPoliciesFailed());
    }
  } finally {
    if (yield cancelled()) {
      controller.abort('Search cancelled by takeLatest');
    }
  }
}

export function* searchPoliciesWorker({ payload }) {
  const controller = new AbortController();

  try {
    const params = {
      ...payload,
      paging: false
    };
    const response = yield call(onGetPolicies, params, controller.signal);
    yield put(searchPoliciesSucceeded(response.data));
  } catch (error) {
    if (error?.code !== AXIOS_CANCEL_CODE) {
      yield put(searchPoliciesFailed());
    }
  } finally {
    if (yield cancelled()) {
      controller.abort('Search cancelled by takeLatest');
    }
  }
}

export function* exportPoliciesWorker({ payload }) {
  const { params } = payload;

  try {
    const loadPoliciesResponse = yield call(onGetPolicies, { ...params, paging: false });
    const policiesIds = loadPoliciesResponse.data.map((response) => response.id);
    const response = yield call(onExportPolicies, policiesIds);

    yield put(exportPoliciesSucceeded());

    const filename = getFileNameFromApiResponseHeader(response.headers, '.xlsx');

    // only IE does support this method
    if (window.navigator.msSaveOrOpenBlob) {
      const blob = new Blob([response.data], { type: 'application/pdf' });
      window.navigator.msSaveBlob(blob, filename);
    } else {
      const blob = new File([response.data], filename, {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64'
      });
      FileSaver.saveAs(blob);
    }
  } catch {
    yield put(exportPoliciesFailed());
  }
}

export function* reloadPoliciesWorker() {
  const activeFilters = yield select(selectPoliciesActiveFilters);
  const parsedActiveFilters = queryString.parse(activeFilters);

  yield all([
    put(getPolicies(parsedActiveFilters)),
    put(getPoliciesInsuredRisksFilterOptions()),
    put(getPoliciesInsuranceOrganizationsFilterOptions()),
    put(getPoliciesInsuredRiskTypesFilterOptions()),
    put(getPoliciesCustomFieldsFilterOptions())
  ]);
}

export default function* policiesSaga() {
  yield all([
    takeLatest(getPolicies.type, getPoliciesWorker),
    takeLatest(searchPolicies.type, searchPoliciesWorker),
    takeEvery(exportPolicies.type, exportPoliciesWorker),
    takeLatest(reloadPolicies.type, reloadPoliciesWorker)
  ]);
}
